import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { ShieldCheck, FileSearch, Coins, ScrollText, Scale, CheckCircle2, AlertTriangle, Clock3 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { k: "registries", label: "Registry Network", Icon: ShieldCheck },
    { k: "dossier", label: "COT Dossier", Icon: FileSearch },
    { k: "insurance", label: "Title Insurance", Icon: Coins },
    { k: "deeds", label: "Deed Generator", Icon: ScrollText },
    { k: "disputes", label: "Dispute Center", Icon: Scale },
];

export default function LegalHub() {
    const [tab, setTab] = useState("registries");
    const [films, setFilms] = useState([]);
    const [filmId, setFilmId] = useState("");
    const [reg, setReg] = useState(null);
    const [dossier, setDossier] = useState(null);
    const [quotes, setQuotes] = useState([]);
    const [coverage, setCoverage] = useState(5000000);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [dispForm, setDispForm] = useState({ category: "chain_of_title", description: "" });
    const [myDisputes, setMyDisputes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/films");
            setFilms(data);
            if (data[0]) setFilmId(data[0].id);
            const j = await api.get("/deed/jurisdictions"); setJurisdictions(j.data);
            try { const md = await api.get("/disputes/mine"); setMyDisputes(md.data); } catch (_) {}
        })();
    }, []);

    useEffect(() => {
        if (!filmId) return;
        (async () => {
            try { const { data } = await api.get(`/registries/${filmId}`); setReg(data); } catch (_) {}
        })();
        setDossier(null); setQuotes([]);
    }, [filmId]);

    const runDossier = async () => {
        if (!filmId) return;
        setLoading(true); setDossier(null);
        try {
            const { data } = await api.post("/dossier/generate", { film_id: filmId });
            setDossier(data.dossier);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
        finally { setLoading(false); }
    };

    const runQuote = async () => {
        if (!filmId) return;
        setLoading(true);
        try {
            const { data } = await api.post("/insurance/quote", {
                film_id: filmId, coverage_usd: Number(coverage), territory: "Worldwide"
            });
            setQuotes(data.quotes);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    const fileDispute = async (e) => {
        e.preventDefault();
        try {
            await api.post("/disputes", { film_id: filmId, ...dispForm });
            toast.success("Dispute filed — AI mediation in progress");
            setDispForm({ category: "chain_of_title", description: "" });
            const md = await api.get("/disputes/mine"); setMyDisputes(md.data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const statusDot = (s) => ({
        clear: "text-emerald-300", green: "text-emerald-300",
        needs_review: "text-amber-300", amber: "text-amber-300",
        missing: "text-red-300", red: "text-red-300",
    })[s] || "text-silver-300";

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> Legal Trust Stack
                </div>
                <h1 className="font-display text-5xl md:text-6xl mb-3 leading-none">The <span className="silver-text italic">trust</span> moat.</h1>
                <p className="text-silver-400 mb-10 max-w-2xl">Registry verification, chain-of-title dossiers, title insurance, jurisdiction-aware deeds, and dispute resolution — all in one place.</p>

                {/* Film picker */}
                <div className="mb-6 flex items-center gap-3 flex-wrap">
                    <span className="text-[11px] tracking-widest uppercase text-silver-500">Active film:</span>
                    <select value={filmId} onChange={(e) => setFilmId(e.target.value)}
                        className="bg-ink-200 border border-white/10 px-3 py-2 text-sm rounded-sm"
                        data-testid="legal-film-picker">
                        {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                    </select>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-6 border-b border-white/5 mb-8 overflow-x-auto">
                    {TABS.map(({ k, label, Icon }) => (
                        <button key={k} onClick={() => setTab(k)}
                            className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap text-xs uppercase tracking-widest transition-colors ${
                                tab === k ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"
                            }`}
                            data-testid={`legal-tab-${k}`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {tab === "registries" && reg && (
                    <div data-testid="registries-panel">
                        <div className="flex items-end justify-between mb-6">
                            <div>
                                <div className="font-display text-3xl silver-text">
                                    {reg.registered_count} <span className="text-silver-500 text-xl">/ {reg.total_available}</span>
                                </div>
                                <div className="text-silver-400 text-sm mt-1">jurisdictions currently verified · {reg.mocked ? "MOCKED integration" : ""}</div>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {reg.registries.map((r) => (
                                <div key={r.code} className="border border-white/10 rounded-sm p-4 bg-ink-200/50">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-silver-200 text-sm">{r.name}</div>
                                        <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                                    </div>
                                    <div className="text-[11px] text-silver-500 tracking-widest uppercase mb-1">{r.country}</div>
                                    <div className="font-mono text-xs text-platinum">{r.registration_number}</div>
                                    <div className="text-[10px] text-silver-500 mt-2">verified {new Date(r.verified_at).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "dossier" && (
                    <div data-testid="dossier-panel">
                        <button onClick={runDossier} disabled={loading}
                            className="bg-platinum hover:bg-white text-ink-400 px-5 py-3 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50"
                            data-testid="dossier-generate-btn">
                            {loading ? "Generating…" : "Generate Dossier (AI)"}
                        </button>
                        {dossier && (
                            <div className="space-y-5">
                                <div className="border border-platinum/30 bg-platinum/5 rounded-sm p-5">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="text-[10px] tracking-[0.3em] uppercase text-silver-500">Confidence</div>
                                        <div className="text-2xl font-display text-platinum">Grade {dossier.overall_confidence}</div>
                                    </div>
                                    <div className="text-silver-200 text-sm">{dossier.summary}</div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                    {dossier.sections.map((s, i) => (
                                        <div key={i} className="border border-white/10 rounded-sm p-4 bg-ink-200/50">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="text-silver-200 text-sm">{s.name}</div>
                                                <span className={`text-[10px] tracking-widest uppercase ${statusDot(s.status)}`}>{s.status.replace("_", " ")}</span>
                                            </div>
                                            <div className="text-silver-400 text-xs">{s.note}</div>
                                        </div>
                                    ))}
                                </div>
                                {dossier.gaps?.length > 0 && (
                                    <div>
                                        <div className="text-[10px] tracking-widest uppercase text-amber-300 mb-2 flex items-center gap-2">
                                            <AlertTriangle className="w-3 h-3" /> Gaps
                                        </div>
                                        <ul className="text-silver-300 text-sm space-y-1">
                                            {dossier.gaps.map((g, i) => <li key={i}>◆ {g}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {tab === "insurance" && (
                    <div data-testid="insurance-panel">
                        <div className="flex flex-wrap items-end gap-4 mb-6">
                            <div>
                                <label className="block text-[10px] tracking-widest uppercase text-silver-500 mb-1">Coverage (USD)</label>
                                <input type="number" value={coverage} onChange={(e) => setCoverage(e.target.value)}
                                    className="bg-ink-200 border border-white/10 px-3 py-2 text-sm rounded-sm w-48" />
                            </div>
                            <button onClick={runQuote} disabled={loading}
                                className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm disabled:opacity-50"
                                data-testid="insurance-quote-btn">
                                {loading ? "Quoting…" : "Get quotes"}
                            </button>
                        </div>
                        {quotes.length > 0 && (
                            <div className="space-y-2">
                                {quotes.map((q, i) => (
                                    <div key={i} className={`flex items-center justify-between p-4 border rounded-sm ${i === 0 ? "border-platinum bg-platinum/5" : "border-white/10 bg-ink-200/40"}`}>
                                        <div>
                                            <div className="text-silver-200 text-sm">{q.insurer}</div>
                                            <div className="text-[10px] text-silver-500 tracking-widest uppercase">{q.term_months}mo · {q.territory}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`font-display text-2xl ${i === 0 ? "silver-text" : "text-silver-200"} tabular-nums`}>${q.premium_usd.toLocaleString()}</div>
                                            <div className="text-[10px] text-silver-500">{q.rate_pct}% rate</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "deeds" && (
                    <div data-testid="deeds-panel">
                        <p className="text-silver-400 mb-6 max-w-2xl text-sm">
                            Section-18 assignment deed auto-switches clauses for each jurisdiction's governing act, with automatic stamp-duty calculation.
                        </p>
                        <div className="grid md:grid-cols-2 gap-3">
                            {jurisdictions.map((j) => (
                                <div key={j.code} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-silver-200">{j.name}</div>
                                        <div className="text-[10px] tracking-widest uppercase text-platinum">{j.code}</div>
                                    </div>
                                    <div className="text-[11px] text-silver-500">{j.act}</div>
                                    <div className="text-[11px] text-silver-400 mt-1">Stamp duty: {j.stamp_duty_pct}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "disputes" && (
                    <div data-testid="disputes-panel" className="grid md:grid-cols-2 gap-8">
                        <form onSubmit={fileDispute} className="border border-white/10 rounded-sm p-6 bg-ink-200/40">
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-4">File a dispute</div>
                            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-1">Category</label>
                            <select value={dispForm.category} onChange={(e) => setDispForm({ ...dispForm, category: e.target.value })}
                                className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4">
                                <option value="chain_of_title">Chain-of-Title</option>
                                <option value="payment">Payment</option>
                                <option value="misrepresentation">Misrepresentation</option>
                                <option value="non_delivery">Non-delivery</option>
                            </select>
                            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-1">Description</label>
                            <textarea value={dispForm.description} onChange={(e) => setDispForm({ ...dispForm, description: e.target.value })}
                                rows={5}
                                className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" required />
                            <button type="submit"
                                className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm"
                                data-testid="dispute-file-btn">File dispute</button>
                            <div className="text-[11px] text-silver-500 mt-3">Flow: AI mediation → 3-attorney panel → SIAC/WIPO arbitration</div>
                        </form>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-4">My disputes</div>
                            {myDisputes.length === 0 ? (
                                <div className="text-silver-500 text-sm">No disputes filed.</div>
                            ) : (
                                <div className="space-y-3">
                                    {myDisputes.map((d) => (
                                        <div key={d.id} className="border border-white/10 rounded-sm p-4">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="text-silver-200 text-sm">{d.category.replace("_", " ")}</div>
                                                <div className="text-[10px] tracking-widest uppercase text-platinum flex items-center gap-1">
                                                    <Clock3 className="w-3 h-3" /> {d.stage}
                                                </div>
                                            </div>
                                            <div className="text-silver-400 text-xs truncate">{d.description}</div>
                                            {d.ai_mediation?.recommendation && (
                                                <div className="text-[11px] text-silver-500 italic mt-2 border-l-2 border-platinum/40 pl-2">
                                                    AI: {d.ai_mediation.recommendation}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
